import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Table from '../components/table/Table';
import JobProtocolButton from '../components/job-protocol/JobProtocolButton';
import { fetchCustomers, fetchJobsPage } from '../lib/crmClient';

const TECHNICI = [
  { id: '1', fullName: 'Tomáš Raška', email: 'tomas.raska@derator.cz' },
  { id: '1007', fullName: 'Pavel Sedláček', email: 'pavel.sedlacek@derator.cz' },
  { id: '1011', fullName: 'Filip Landovský', email: 'filip.landovsky@derator.cz' },
  { id: '1012', fullName: 'Libor Landovský', email: 'libor.landovsky@derator.cz' },
  { id: '1013', fullName: 'Jaromír Mareš', email: 'jaromir.mares@derator.cz' },
  { id: '1027', fullName: 'Petr Sedláček', email: 'petr.sedlacek@derator.cz' },
  { id: '1042', fullName: 'Radek Steppan', email: 'radek.steppan@derator.cz' },
  { id: '1044', fullName: 'Adam Duda', email: 'adam.duda@derator.cz' },
  { id: '1048', fullName: 'Vladimír Nedopil', email: 'vladimir.nedopil@derator.cz' },
  { id: '1050', fullName: 'Lukáš Franěk', email: 'lukas.franek@derator.cz' },
];

const SKUDCI = [
  { id: 1, label: 'Deratizace' },
  { id: 2, label: 'Dezinsekce' },
  { id: 3, label: 'Dezinfekce' },
  { id: 4, label: 'Myš, potkan, krysa' },
  { id: 5, label: 'Rus, Šváb' },
  { id: 6, label: 'Štěnice' },
  { id: 7, label: 'Vosy, včely, sršni' },
  { id: 8, label: 'Blechy' },
  { id: 9, label: 'Mouhy, pavouci' },
  { id: 10, label: 'Ostatní' },
  { id: 11, label: 'Mravenci' },
  { id: 12, label: 'Fumigace' },
];

const normalize = (value) => String(value || '').trim().toLowerCase();
const PAGE_SIZE = 100;
const SEARCH_PAGE_SIZE = 500;
const PAGE_REQUEST_GAP_MS = 120;

const getMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const getJobDateMonth = (item) => String(item.DatumNaplanovano || item.Datum || '').slice(0, 7);

const mergeUniqueJobs = (base, extra) => {
  const seen = new Set((base || []).map((item) => String(item.ZakazkaId)));
  const added = (extra || []).filter((item) => !seen.has(String(item.ZakazkaId)));
  return [...(base || []), ...added];
};

const mergeSavedJobIntoList = (jobs, savedJob) => {
  if (!savedJob || !savedJob.ZakazkaId) return jobs || [];

  const normalizedSavedId = String(savedJob.ZakazkaId);
  const base = Array.isArray(jobs) ? jobs : [];
  const existingIndex = base.findIndex((item) => String(item.ZakazkaId) === normalizedSavedId);

  if (existingIndex === -1) {
    return [savedJob, ...base];
  }

  const next = [...base];
  next[existingIndex] = { ...next[existingIndex], ...savedJob };
  return next;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return `${Math.round(n).toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} Kč`;
};

const formatDateCz = (value) => {
  if (!value) return '-';
  const raw = String(value).slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
};

const paymentLabel = (paymentId) => {
  const id = Number(paymentId);
  if (id === 1) return 'Hotově';
  if (id === 2) return 'Faktura';
  return '-';
};

const skudceLabel = (item) => {
  if (item && item.Skudce) return item.Skudce;
  const id = Number(item && item.SkudceId);
  const found = SKUDCI.find((s) => s.id === id);
  return found ? found.label : '-';
};

const technicianName = (item) => {
  const byId = TECHNICI.find((tech) => String(tech.id) === String(item && item.ZamestnanecId));
  if (byId) return byId.fullName.split(' ')[0] || byId.fullName;
  const byEmail = TECHNICI.find((tech) => normalize(tech.email) === normalize(item && item.TechnikEmail));
  if (!byEmail) return '-';
  return byEmail.fullName.split(' ')[0] || byEmail.fullName;
};

const Zakazky = ({ email }) => {
  const location = useLocation();
  const savedJobFromNavigation = location.state && location.state.savedJob ? location.state.savedJob : null;

  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [canViewAll, setCanViewAll] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('all');
  const [payment, setPayment] = useState('all');
  const [technikId, setTechnikId] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const showTechnicianFilter = canViewAll;
  const hasSearch = search.trim().length > 0;
  const activeSearchRequestRef = useRef(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showTechnicianFilter) setTechnikId('all');
  }, [showTechnicianFilter]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const customerList = await fetchCustomers();
        if (!cancelled) {
          setCustomers(customerList);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Načtení zákazníků selhalo');
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const requestId = activeSearchRequestRef.current + 1;
    activeSearchRequestRef.current = requestId;
    setLoadingMore(false);

    const run = async () => {
      const selectedTechnik = TECHNICI.find((item) => item.id === technikId) || null;
      const technicianEmail = canViewAll && selectedTechnik ? selectedTechnik.email : '';

      if (!cancelled) {
        setLoading(true);
        setError('');
      }

      try {
        const pageSize = search.trim() ? SEARCH_PAGE_SIZE : PAGE_SIZE;
        const result = await fetchJobsPage({
          email,
          technicianEmail,
          search,
          page: 1,
          pageSize,
          signal: controller.signal,
        });

        let mergedItems = result.items || [];
        let nextHasMore = Boolean(result.hasMore);
        let loadedPage = 1;
        const q = search.trim().toLowerCase();

        // During search, always load all pages so results are not limited by pagination.
        while (!cancelled && q && nextHasMore) {
          await delay(PAGE_REQUEST_GAP_MS);
          const nextPage = loadedPage + 1;
          const next = await fetchJobsPage({
            email,
            technicianEmail,
            search,
            page: nextPage,
            pageSize,
            signal: controller.signal,
          });

          if (!next.items || next.items.length === 0) {
            nextHasMore = false;
            break;
          }

          const beforeCount = mergedItems.length;
          mergedItems = mergeUniqueJobs(mergedItems, next.items || []);
          if (mergedItems.length === beforeCount) {
            nextHasMore = false;
            break;
          }
          nextHasMore = Boolean(next.hasMore);
          loadedPage = nextPage;
        }

        if (!cancelled && activeSearchRequestRef.current === requestId) {
          setCanViewAll(Boolean(result.canViewAll));
          setJobs(q ? mergedItems : mergeSavedJobIntoList(mergedItems, savedJobFromNavigation));
          setHasMore(q ? false : nextHasMore);
          setPage(loadedPage);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Načtení zakázek selhalo');
          setJobs([]);
          setHasMore(false);
          setPage(1);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [canViewAll, email, technikId, search, savedJobFromNavigation]);

  const loadMore = async () => {
    if (loadingMore || loading || !hasMore) return;

    const selectedTechnik = TECHNICI.find((item) => item.id === technikId) || null;
    const technicianEmail = canViewAll && selectedTechnik ? selectedTechnik.email : '';
    const nextPage = page + 1;
    const requestId = activeSearchRequestRef.current;

    setLoadingMore(true);
    setError('');

    try {
      const result = await fetchJobsPage({
        email,
        technicianEmail,
        search,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });

      if (activeSearchRequestRef.current !== requestId) return;
      setCanViewAll(Boolean(result.canViewAll));
      setJobs((prev) => mergeUniqueJobs(prev, result.items || []));
      setHasMore(Boolean(result.hasMore));
      setPage(nextPage);
    } catch (e) {
      if (activeSearchRequestRef.current !== requestId) return;
      setError(e instanceof Error ? e.message : 'Načtení dalších zakázek selhalo');
    } finally {
      if (activeSearchRequestRef.current === requestId) setLoadingMore(false);
    }
  };

  const customerMap = useMemo(() => {
    const map = new Map();
    customers.forEach((c) => map.set(c.ZakaznikId, c));
    return map;
  }, [customers]);

  const filtered = useMemo(() => {
    const selectedTechnik = TECHNICI.find((item) => item.id === technikId) || null;
    const currentMonth = getMonthKey(new Date());
    const previousMonthDate = new Date();
    previousMonthDate.setDate(1);
    previousMonthDate.setMonth(previousMonthDate.getMonth() - 1);
    const previousMonth = getMonthKey(previousMonthDate);

    return jobs
      .filter((item) => {
        const jobMonth = getJobDateMonth(item);
        if (period === 'current' && jobMonth !== currentMonth) return false;
        if (period === 'previous' && jobMonth !== previousMonth) return false;

        if (showTechnicianFilter && selectedTechnik) {
          const itemEmployeeId = String(item.ZamestnanecId || '').trim();
          const itemEmail = normalize(item.TechnikEmail);
          const matchById = itemEmployeeId && itemEmployeeId === selectedTechnik.id;
          const matchByEmail = itemEmail && itemEmail === normalize(selectedTechnik.email);
          if (!matchById && !matchByEmail) return false;
        }

        const paymentId = String(item.ZpusobPlatbyId || '');
        if (payment !== 'all' && paymentId !== payment) return false;

        return true;
      })
      .sort((a, b) => (b.ZakazkaId || 0) - (a.ZakazkaId || 0));
  }, [jobs, payment, period, showTechnicianFilter, technikId]);

  const mobileRows = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aDate = new Date(a.DatumNaplanovano || a.Datum || 0).getTime();
      const bDate = new Date(b.DatumNaplanovano || b.Datum || 0).getTime();
      return bDate - aDate;
    });
  }, [filtered]);

  return (
    <div>
      <h2 className="page-header">Přehled zakázek</h2>

      <div className="card crm-card--jobs-list">
        <div className={`crm-toolbar ${showTechnicianFilter ? 'crm-toolbar--jobs-all' : ''}`}>
          <input
            aria-label="Hledat zakázky"
            placeholder="Hledat podle názvu, zákazníka, adresy, popisu..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearch(searchInput.trim());
            }}
          />
          <select aria-label="Období zakázek" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="current">Aktuální měsíc</option>
            <option value="previous">Minulý měsíc</option>
            <option value="all">Všechny zakázky</option>
          </select>
          {(!isMobile || payment !== 'all') ? (
            <select aria-label="Způsob platby" value={payment} onChange={(e) => setPayment(e.target.value)}>
              <option value="all">Všechny platby</option>
              <option value="1">Hotově</option>
              <option value="2">Faktura</option>
            </select>
          ) : null}
          {showTechnicianFilter ? (
            <select aria-label="Technik" value={technikId} onChange={(e) => setTechnikId(e.target.value)}>
              <option value="all">Všichni technici</option>
              {TECHNICI.map((item) => (
                <option key={item.id} value={item.id}>{item.fullName.split(' ')[0] || item.fullName}</option>
              ))}
            </select>
          ) : null}
          <Link className="btn btn-primary" to="/zakazky/nova">
            Nová zakázka
          </Link>
        </div>

        {error ? <p className="crm-error" style={{ marginTop: 15 }}>{error}</p> : null}
        {loading ? <p style={{ marginTop: 15 }}>Načítám...</p> : null}
        {!loading && !error && filtered.length === 0 ? <p role="status" style={{ marginTop: 15 }}>Žádné zakázky neodpovídají výběru.</p> : null}

        {isMobile ? (
          <div className="crm-mobile-job-list">
            {mobileRows.map((item) => {
              const customer = customerMap.get(item.ZakaznikId);
              const dateValue = item.DatumNaplanovano || item.Datum || '-';
              const nameValue = item.NazevZakaznika || item.NazevZakazky || (customer ? customer.Nazev : 'Bez zákazníka');
              const skudceValue = skudceLabel(item);

              return (
                <div key={item.ZakazkaId} className="crm-mobile-job-row crm-mobile-job-row--job-list">
                  <div className="crm-mobile-job-row__meta">
                    <span className="crm-mobile-job-date">{formatDateCz(dateValue)}</span>
                    <span className="crm-mobile-job-pest" title={skudceValue || '-'}>{skudceValue || '-'}</span>
                  </div>
                  <div className="crm-mobile-job-row__top">
                    <span className="crm-mobile-job-name" title={nameValue || '-'}>{nameValue || '-'}</span>
                  </div>
                  <div className="crm-mobile-job-row__bottom crm-mobile-job-row__bottom--inline">
                    <div className="crm-mobile-job-actions">
                      <span className="crm-mobile-job-price">{formatCurrency(item.CenaZakazky)}</span>
                      <JobProtocolButton job={item} />
                      <Link
                        className="btn btn-blue table-action-btn table-action-btn--mobile"
                        to={{ pathname: `/zakazky/${item.ZakazkaId}`, state: { returnTo: { pathname: '/zakazky' } } }}
                      >
                        Upravit
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Table
            headData={['Datum', 'Zákazník', 'Adresa', 'Škůdce', 'Technik', 'Cena', 'Doprava', 'Způsob platby', 'Akce']}
            renderHead={(item, index) => <th key={index}>{item}</th>}
            bodyData={filtered}
            renderBody={(item) => {
              const customer = customerMap.get(item.ZakaznikId);
              const dateValue = item.DatumNaplanovano || item.Datum || '-';

              return (
                <tr key={item.ZakazkaId}>
                  <td>{formatDateCz(dateValue)}</td>
                  <td>{item.NazevZakaznika || (customer ? customer.Nazev : 'Bez zákazníka')}</td>
                  <td>{customer ? customer.Ulice || '-' : '-'}</td>
                  <td>{skudceLabel(item)}</td>
                  <td>{technicianName(item)}</td>
                  <td>{formatCurrency(item.CenaZakazky)}</td>
                  <td>{item.Doprava !== null && item.Doprava !== undefined ? `${item.Doprava} km` : '-'}</td>
                  <td>{paymentLabel(item.ZpusobPlatbyId)}</td>
                  <td>
                    <div className="table-action-row">
                      <JobProtocolButton job={item} />
                      <Link
                        className="btn table-action-icon-btn"
                        to={{ pathname: '/zakazky/nova', search: `?copyFrom=${item.ZakazkaId}`, state: { returnTo: { pathname: '/zakazky' } } }}
                        title={`Kopírovat zakázku #${item.ZakazkaId}`}
                        aria-label={`Kopírovat zakázku #${item.ZakazkaId}`}
                      >
                        <i className="bx bx-copy-alt"></i>
                      </Link>
                      <Link
                        className="btn btn-blue table-action-icon-btn"
                        to={{ pathname: `/zakazky/${item.ZakazkaId}`, state: { returnTo: { pathname: '/zakazky' } } }}
                        title={`Upravit zakázku #${item.ZakazkaId}`}
                        aria-label={`Upravit zakázku #${item.ZakazkaId}`}
                      >
                        <i className="bx bx-edit-alt" aria-hidden="true" />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            }}
          />
        )}

        {!hasSearch ? (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            {hasMore ? (
              <button type="button" className="btn btn-blue" disabled={loadingMore || loading} onClick={loadMore}>
                {loadingMore ? 'Načítám další...' : 'Načíst další zakázky'}
              </button>
            ) : (
              <span style={{ color: '#777' }}>Další zakázky nejsou k dispozici</span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Zakazky;
